/**
 * KLAVIYO FACET DE-DUPLICATION
 * -----------------------------------------------------------------------------------------------
 * Klaviyo's onsite script hijacks history.pushState/replaceState so it can re-show its onsite forms
 * on client-side (SPA-style) navigation. The theme's faceting updates the address bar via
 * history.replaceState on every filter (see the facet:update handler in theme.js), so Klaviyo thinks
 * a new page loaded and RE-INJECTS a copy of each embedded form into its `.klaviyo-form-XXXX`
 * container — without removing the copy already there. The result is stacked duplicate forms.
 *
 * This happens on EVERY faceting path (drawer, sidebar, swatch bar, colour pills) because they all
 * share the same history.replaceState. This safeguard is therefore global: after any facet update we
 * briefly watch the DOM and collapse each Klaviyo embed back to a single rendered form.
 *
 * It only touches Klaviyo embed containers — it does not modify the History API or Klaviyo tracking.
 */
(function () {
  const SELECTOR = 'div[class*="klaviyo-form-"]';
  let observer = null;
  let stopTimer = null;

  function dedupe() {
    if (!document.querySelector(SELECTOR)) return;

    const seen = new Set();
    document.querySelectorAll(SELECTOR).forEach((container) => {
      const key = (container.className.match(/klaviyo-form-[A-Za-z0-9]+/) || [])[0];
      if (!key) return;

      // A second container for the same form → keep the first, drop the rest.
      if (seen.has(key)) {
        container.remove();
        return;
      }
      seen.add(key);

      // Klaviyo renders exactly one form per embed; any extra children are re-injected duplicates.
      // Keep the first (the one the visitor may already be interacting with), remove the rest.
      while (container.childElementCount > 1) {
        container.removeChild(container.lastElementChild);
      }
    });
  }

  function armAfterFacetUpdate() {
    if (!document.querySelector(SELECTOR)) return; // no Klaviyo embeds on this page

    if (!observer) {
      observer = new MutationObserver(() => {
        // Disconnect while we mutate so our own removals don't retrigger the observer.
        observer.disconnect();
        dedupe();
        observer.observe(document.body, { childList: true, subtree: true });
      });
    }

    clearTimeout(stopTimer);
    observer.disconnect();
    dedupe(); // immediate pass
    observer.observe(document.body, { childList: true, subtree: true });

    // Klaviyo re-injects asynchronously; watch for a short window, then stop (self-terminating).
    stopTimer = setTimeout(() => observer && observer.disconnect(), 4000);
  }

  document.addEventListener('facet:update', armAfterFacetUpdate);
})();
